/*
 * GetCustomPack large-order quote suggestion — isolated theme UI, 2026-10-23 v3 (6000 units, parent-company and cart reminder).
 * This script only reads Shopify's Ajax cart. It never writes cart data,
 * intercepts PackQ, changes pricing, intercepts checkout, or styles the page.
 * All popup styling lives inside a Shadow DOM.
 */
(() => {
  'use strict';

  function init() {
    const configNode = document.getElementById('gcp-volume-prompt-settings');
    if (!configNode || document.getElementById('gcp-volume-prompt-host')) return;

    const cfg = configNode.dataset;
    const configuredThreshold = Number.parseInt(cfg.threshold, 10);
    const threshold = Number.isSafeInteger(configuredThreshold) && configuredThreshold > 0
      ? configuredThreshold : 6000;
    const cartUrl = cfg.cartUrl || '/cart.js';
    const designMode = cfg.designMode === 'true';
    const showPreview = designMode && cfg.editorPreview === 'true' && !document.querySelector('[data-gcp-project-form]');
    const isCartPage = /\/cart\/?$/.test(window.location.pathname);
    const storageKey = `gcp-volume-prompt-notified-v3-${threshold}`;
    const reminderEnabled = cfg.checkoutReminderEnabled === 'true';
    const history = new Map();

    try {
      const raw = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
      if (Array.isArray(raw)) {
        raw.forEach((pair) => {
          if (Array.isArray(pair) && typeof pair[0] === 'string' && Number.isSafeInteger(pair[1])) {
            history.set(pair[0], pair[1]);
          }
        });
      }
    } catch (_) { /* Storage may be disabled; in-memory deduplication still works. */ }

    const persist = () => {
      try { sessionStorage.setItem(storageKey, JSON.stringify([...history])); } catch (_) {}
    };
    const safeUrl = (input, fallback) => {
      if (!String(input || '').trim()) return fallback;
      try {
        const u = new URL(String(input || '').trim(), window.location.origin);
        if (u.protocol === 'https:' || (u.protocol === 'http:' && u.origin === window.location.origin)) return u.href;
      } catch (_) {}
      return fallback;
    };

    // A Shadow DOM prevents global theme CSS (especially generic dialog, a,
    // button and cart rules) from leaking into or out of this component.
    const host = document.createElement('div');
    host.id = 'gcp-volume-prompt-host';
    const root = host.attachShadow({mode: 'open'});
    root.innerHTML = `
      <style>
        :host { display: contents; }
        *, *::before, *::after { box-sizing: border-box; }
        dialog {
          position: fixed; inset: 0; margin: auto; width: min(92vw, 790px);
          max-width: 790px; max-height: min(91dvh, 760px); overflow-y: auto;
          overscroll-behavior: contain; background: #fff; color: #203265;
          border: 1px solid #dae3f2; border-radius: 18px;
          box-shadow: 0 26px 85px rgba(11, 28, 59, .28);
          padding: 0; font: 400 15px/1.55 system-ui, -apple-system, "Segoe UI", Arial, sans-serif;
          letter-spacing: normal; text-align: left;
        }
        dialog::backdrop { background: rgba(17, 36, 64, .59); }
        .panel { padding: 30px 32px 25px; }
        .close { position: absolute; top: 13px; right: 13px; width: 38px; height: 38px;
          border: 1px solid #dce5f1; border-radius: 50%; display: grid; place-items: center;
          background: #f7f9fd; color: #203265; cursor: pointer; line-height: 1; font-size: 23px; }
        .close:hover { background: #e9effa; }
        .eyebrow { margin: 0 42px 9px 0; color: #366fc2; font-size: 12px;
          font-weight: 750; text-transform: uppercase; letter-spacing: .08em; }
        h2 { margin: 0 44px 10px 0; color: #203265; font-size: clamp(22px, 3.3vw, 29px);
          font-weight: 750; line-height: 1.22; letter-spacing: -.025em; }
        .rule { height: 3px; width: 46px; border-radius: 8px; background: #f77317; margin-bottom: 14px; }
        .summary { margin: 0 0 8px; color: #203265; font-size: 15px; line-height: 1.45; }
        .summary strong { font-weight: 760; }
        .detail { margin: 0 0 10px; font-size: 15px; color: #52637a; }
        .intro { color: #53667f; margin: 0 0 20px; font-size: 16px; line-height: 1.65; }
        .choices { display: grid; grid-template-columns: minmax(0,1fr) minmax(0,1fr); gap: 14px; }
        .choice { min-width: 0; padding: 18px 17px 17px; border: 1px solid;
          border-radius: 12px; display: flex; flex-direction: column; }
        .brown { background: #fff; border-color: #203265; }
        .gcp { background: #f5f7fc; border-color: #cbd8f0; }
        .logo { height: 56px; display: flex; align-items: center; margin-bottom: 9px; max-width: 100%; }
        .logo img { display: block; max-height: 54px; max-width: 100%; width: auto; height: auto; object-fit: contain; }
        .logo-fallback { display: none; font-size: 17px; font-weight: 800; }
        .brown .logo-fallback { color: #1a4359; }
        .gcp .logo-fallback { color: #203265; }
        .parent-label { display: inline-block; align-self: flex-start; margin: 0 0 8px;
          padding: 3px 8px; border-radius: 4px; font-size: 11px; line-height: 1.3;
          font-weight: 750; letter-spacing: .025em; color: #203265; background: #edf3fb; }
        .choice h3 { margin: 0 0 7px; font-size: 18px; font-weight: 740; line-height: 1.3; }
        .brown h3 { color: #203265; }
        .gcp h3 { color: #203265; }
        .choice p { margin: 0 0 16px; color: #526778; font-size: 16px; line-height: 1.6; flex: 1; }
        .action { min-height: 46px; display: flex; justify-content: center; align-items: center;
          width: 100%; padding: 11px 12px; border-radius: 8px; border: 1.5px solid;
          font: 700 16px/1.4 system-ui, -apple-system, "Segoe UI", Arial, sans-serif;
          text-align: center; cursor: pointer; text-decoration: none;
          transition: color .16s ease, background-color .16s ease; }
        .brown .action { color: #fff; background: #f77317; border-color: #203265; }
        .brown .action:hover { background: #366fc2; color: #fff; }
        .visit-brown{display:block;margin-top:12px;text-align:center;color:#366fc2;text-decoration:underline;font-size:14px;font-weight:650}
        .gcp .action { color: #fff; background: #366fc2; border-color: #203265; }
        .gcp .action:hover { background: transparent; color: #366fc2; }
        .visually-hidden{position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%)}
        .note { text-align: center; margin: 17px 0 0; padding-top: 13px;
          border-top: 1px solid #e1e9f2; color: #63748b; font-size: 15px; }
        button:focus-visible, a:focus-visible { outline: 3px solid #f77317; outline-offset: 3px; }
        .preview { position: fixed; z-index: 2147480001; left: 17px; bottom: 18px;
          max-width: calc(100vw - 34px); border: 1px solid #203265; border-radius: 10px;
          background: #fff; color: #203265; box-shadow: 0 5px 19px rgba(20,40,70,.16);
          padding: 10px 14px; font: 700 12px/1.3 system-ui, sans-serif; cursor: pointer; }
        @media (max-width: 620px) {
          dialog { width: min(94vw, 460px); max-height: 92dvh; border-radius: 13px; }
          .panel { padding: 23px 17px 16px; }
          .choices { grid-template-columns: minmax(0, 1fr); gap: 10px; }
          .choice { padding: 11px 14px; }
          .logo { height: 37px; margin-bottom: 4px; }
          .logo img { max-height: 35px; }
          .choice h3 { font-size: 15px; margin-bottom: 4px; }
          .parent-label { margin-bottom: 5px; }
          .choice p { margin-bottom: 8px; font-size: 15px; }
          .action { min-height: 41px; padding: 9px 10px; }
          .intro { margin-bottom: 12px; }
          .note { margin-top: 11px; }
          .close { top: 8px; right: 8px; }
        }
        @media (prefers-reduced-motion: reduce) { .action { transition: none; } }
      </style>
      <dialog aria-modal="true" aria-labelledby="volume-heading" aria-describedby="volume-intro">
        <div class="panel">
          <button class="close" type="button" aria-label="Close suggestion">×</button>
          <p class="eyebrow">Larger order options</p>
          <h2 id="volume-heading"></h2>
          <div class="rule" aria-hidden="true"></div>
          <p class="summary"><strong id="volume-item"></strong></p>
          <p class="detail" id="volume-detail"></p>
          <p class="intro" id="volume-intro"></p>
          <div class="choices">
            <div class="choice brown">
              <div class="logo"><img id="brown-logo" alt="Brown Packaging" loading="eager"><span class="logo-fallback">Brown Packaging</span></div>
              <span id="brown-parent-label" class="parent-label"></span>
              <h3 id="brown-project-heading"></h3>
              <p id="brown-project-description"></p>
              <a id="brown-quote" class="action"></a>
              <a id="brown-visit" class="visit-brown" target="_blank" rel="noopener noreferrer">Visit Brown Packaging ↗<span class="visually-hidden"> (opens in a new tab)</span></a>
            </div>
            <div class="choice gcp">
              <div class="logo"><img id="gcp-logo" alt="GetCustomPack" loading="eager"><span class="logo-fallback">GetCustomPack</span></div>
              <h3>Continue your online order</h3>
              <p>Keep your existing cart and artwork. GetCustomPack pricing and checkout stay unchanged.</p>
              <button class="action" id="gcp-continue" type="button"></button>
            </div>
          </div>
          <p class="note">Review your project before sending. Your GetCustomPack cart stays available.</p>
        </div>
      </dialog>
      ${showPreview ? '<button class="preview" type="button">Preview large-order suggestion</button>' : ''}
    `;
    document.body.appendChild(host);

    const dialog = root.querySelector('dialog');
    const closeBtn = root.querySelector('.close');
    const continueBtn = root.getElementById('gcp-continue');
    const quoteLink = root.getElementById('brown-quote');
    const itemName = root.getElementById('volume-item');
    const detail = root.getElementById('volume-detail');
    const formatter = new Intl.NumberFormat('en-US');
    let lastFocused = null;
    let timer = 0;
    let inFlight = false;
    let rescan = false;

    root.getElementById('volume-heading').textContent = cfg.title || 'Your order may qualify for volume pricing';
    root.getElementById('brown-parent-label').textContent = cfg.parentLabel || 'Parent company of GetCustomPack';
    root.getElementById('volume-intro').textContent = cfg.description || '';
    root.getElementById('brown-project-heading').textContent = cfg.handoffHeading || 'Bring your cart items with you';
    root.getElementById('brown-project-description').textContent = cfg.handoffDescription || '';
    continueBtn.textContent = cfg.gcpCta || 'Continue with GetCustomPack';
    quoteLink.textContent = cfg.brownCta || 'Request Brown Packaging pricing';
    quoteLink.href = safeUrl(cfg.brownUrl, 'https://brownpackaging.com/contact-us-2/');
    if(window.GCPBrownQuote)quoteLink.href=window.GCPBrownQuote.quotePage;
    quoteLink.target='_blank';quoteLink.rel='noopener noreferrer';
    quoteLink.setAttribute('aria-label',quoteLink.textContent+' (opens in a new tab)');
    root.getElementById('brown-visit').href=safeUrl(cfg.brownWebsite,'https://brownpackaging.com/');

    [['brown-logo', cfg.brownLogo], ['gcp-logo', cfg.gcpLogo]].forEach(([id, imageUrl]) => {
      const img = root.getElementById(id);
      img.src = safeUrl(imageUrl, '');
      const fallback = img.parentElement.querySelector('.logo-fallback');
      img.addEventListener('error', () => { img.style.display = 'none'; fallback.style.display = 'inline'; });
      if (!img.src || img.src === window.location.href) {
        img.style.display = 'none'; fallback.style.display = 'inline';
      }
    });

    const close = () => { if (dialog.open) dialog.close(); };
    closeBtn.addEventListener('click', close);
    continueBtn.addEventListener('click', close);
    let quoteLine = null;
    quoteLink.addEventListener('click', (event) => {
      if (window.GCPBrownQuote && quoteLine) {
        event.preventDefault();
        if(quoteLink.hasAttribute('aria-busy'))return;
        quoteLink.setAttribute('aria-busy','true');
        const previous=root.querySelector('.quote-error');if(previous)previous.remove();
        window.GCPBrownQuote.open({cartLine: quoteLine}).catch(error=>{
          const note=document.createElement('p');note.className='quote-error';note.setAttribute('role','alert');
          note.style.cssText='padding:12px;margin:12px 0 0;background:#f7f3ec;color:#203265;border-left:3px solid #f77317;font-size:15px;line-height:1.5';
          note.textContent=window.GCPBrownQuote.errorMessage(error);quoteLink.after(note);
        }).finally(()=>quoteLink.removeAttribute('aria-busy'));
      }
    });
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    dialog.addEventListener('close', () => {
      if (lastFocused?.isConnected) lastFocused.focus({preventScroll: true});
      lastFocused = null;
    });

    const property = (properties, name) => {
      if (!properties) return '';
      if (Array.isArray(properties)) {
        const match = properties.find((x) => x?.name === name || x?.[0] === name);
        return match?.value ?? match?.[1] ?? '';
      }
      return properties[name] || '';
    };
    const isPackQ = (item) => {
      const props = item?.properties;
      return Boolean(
        property(props, '_x1MainProductHandle') ||
        property(props, '_x1SelectionId') ||
        property(props, '_gcpPackQProfile')
      );
    };
    const identity = (item, index) => String(
      property(item.properties, '_x1SelectionId') ||
      property(item.properties, '_gcpPackQIdentity') ||
      item.key || `${item.product_id}:${index}`
    );
    const candidateName = (item) => String(item.product_title || item.title || 'custom packaging');

    // A *non-blocking* final reminder is inserted only in the real cart summary.
    // There is no click/submit interception and no checkout-page code.
    let reminderHost = null;
    let reminderEligible = [];
    const syncCheckoutReminder = (eligible) => {
      if (!isCartPage || !reminderEnabled) return;
      reminderEligible = eligible;
      if (!eligible.length) { reminderHost?.remove(); return; }
      const checkoutButton = document.querySelector('.gcp-cart-page .gcp-cart-summary .gcp-cart-checkout');
      if (!checkoutButton) return;
      if (!reminderHost) {
        reminderHost = document.createElement('div');
        reminderHost.id = 'gcp-volume-checkout-reminder';
        const reminderRoot = reminderHost.attachShadow({mode: 'open'});
        reminderRoot.innerHTML = `
          <style>
            :host { display: block; width: 100%; margin: 0 0 14px; }
            *, *::before, *::after { box-sizing: border-box; }
            .reminder { border: 1px solid #b9dce5; border-left: 3px solid #009abe;
              border-radius: 9px; background: #f4fafc; padding: 13px 14px;
              display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
              font: 400 13px/1.45 system-ui, -apple-system, "Segoe UI", Arial, sans-serif; }
            .copy { flex: 1 1 185px; min-width: 0; }
            strong { display: block; margin-bottom: 3px; color: #1a4359; font-weight: 750; }
            p { margin: 0; color: #4b6876; }
            button { flex: 0 0 auto; min-height: 38px; max-width: 100%;
              border: 1px solid #1a4359; border-radius: 7px; background: #fff;
              color: #1a4359; font: 700 12px/1.3 system-ui, sans-serif;
              padding: 9px 12px; cursor: pointer; }
            button:hover { background: #e1f2f6; }
            button:focus-visible { outline: 3px solid #f77317; outline-offset: 3px; }
            @media(max-width: 600px) { .reminder { padding: 12px; } button { width: 100%; flex-basis: 100%; } }
          </style>
          <div class="reminder" role="note">
            <div class="copy"><strong class="title"></strong><p class="description"></p></div>
            <button type="button" class="show-options"></button>
          </div>
        `;
        reminderRoot.querySelector('.title').textContent = cfg.checkoutReminderTitle || 'One more option before checkout';
        reminderRoot.querySelector('.description').textContent = cfg.checkoutReminderDescription ||
          'Brown Packaging, the parent company of GetCustomPack, may have volume pricing options for your larger order.';
        reminderRoot.querySelector('.show-options').textContent = cfg.checkoutReminderButton || 'Review volume pricing options';
        reminderRoot.querySelector('.show-options').addEventListener('click', () => {
          if (reminderEligible.length) openPrompt(reminderEligible[0], reminderEligible.length - 1);
        });
      }
      if (reminderHost.nextElementSibling !== checkoutButton) checkoutButton.before(reminderHost);
    };

    const openPrompt = (line, alsoCount = 0) => {
      if (dialog.open || typeof dialog.showModal !== 'function') return false;
      quoteLine = line;
      const quantity = Number(line.item.quantity);
      itemName.textContent = `${formatter.format(quantity)} × ${candidateName(line.item)}`;
      detail.textContent = `This configured item exceeds the ${formatter.format(threshold)}-unit suggestion threshold.` +
        (alsoCount > 0 ? ` ${alsoCount} other item${alsoCount === 1 ? '' : 's'} also exceed the threshold.` : '');
      lastFocused = root.activeElement || document.activeElement;
      try { dialog.showModal(); closeBtn.focus({preventScroll: true}); return true; }
      catch (_) { return false; }
    };

    const scan = async () => {
      if (dialog.open) return;
      if (inFlight) { rescan = true; return; }
      inFlight = true;
      try {
        const response = await fetch(cartUrl, {credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json'}});
        if (!response.ok) return;
        const cart = await response.json();
        if (!Array.isArray(cart.items)) return;
        const eligible = [];
        const active = new Set();
        cart.items.forEach((item, index) => {
          if (!isPackQ(item)) return;
          const key = identity(item, index);
          active.add(key);
          const qty = Number(item.quantity);
          if (!Number.isInteger(qty) || qty <= threshold) {
            history.delete(key); // Re-arm if they lower the quantity back under the threshold.
            return;
          }
          eligible.push({item, key, qty});
        });
        [...history.keys()].forEach((key) => { if (!active.has(key)) history.delete(key); });
        persist();
        syncCheckoutReminder(eligible);
        const fresh = eligible.find((entry) => history.get(entry.key) !== entry.qty);
        if (fresh && openPrompt(fresh, eligible.length - 1)) {
          // Mark all matching items to avoid cascading popups for multi-item carts.
          eligible.forEach((entry) => history.set(entry.key, entry.qty));
          persist();
        }
      } catch (_) {
        // Optional informational UI must never produce a customer-facing cart failure.
      } finally {
        inFlight = false;
        if (rescan) { rescan = false; scheduleScan(320); }
      }
    };

    function scheduleScan(wait = 280) {
      clearTimeout(timer);
      timer = window.setTimeout(scan, wait);
    }

    // Add-to-cart and drawer events, including PackQ's managed add handoff.
    // Fetch the *confirmed* Shopify cart after the operation; never use the
    // unconfirmed number typed into a quantity field.
    ['on:cart:add', 'dispatch:cart-drawer:refresh', 'dispatch:cart-drawer:open',
      'on:cart-drawer:after-open', 'on:line-item:change'].forEach((eventName) => {
      document.addEventListener(eventName, () => scheduleScan(420));
    });

    // PackQ direct quantity editing is asynchronous. This flag is written only
    // after the actual PackQ/Shopify replacement succeeded, so a failed repricing
    // attempt cannot falsely trigger the suggestion.
    const statusObserver = new MutationObserver(() => {
      // The full-cart quantity handler immediately reloads after success.
      // Wait for that new page instead of briefly opening a disposable modal.
      if (isCartPage) return;
      if (document.documentElement.dataset.gcpPackqCartQuantityEdit === 'updated') scheduleScan(300);
    });
    statusObserver.observe(document.documentElement, {
      attributes: true, attributeFilter: ['data-gcp-packq-cart-quantity-edit']
    });

    // Initially inspect cart only on /cart or when the drawer is already open.
    // Do not interrupt customers simply browsing a product page.
    const drawerOpen = () => Boolean(document.querySelector('cart-drawer[open]'));
    if (isCartPage || drawerOpen()) scheduleScan(100);
    window.addEventListener('pageshow', () => {
      if (isCartPage || drawerOpen()) scheduleScan(200);
    });
    document.addEventListener('shopify:section:load', () => {
      if (isCartPage || drawerOpen()) scheduleScan(300);
    });

    if (showPreview) {
      root.querySelector('.preview')?.addEventListener('click', () => {
        openPrompt({item: {quantity: threshold + 500, product_title: 'Custom Roll End Tuck Top Mailer Box (RETT)'}}, 0);
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
